/**
 * Single-tile maximize (kebab "Expand tile" / title double-click): a large
 * overlay dialog re-rendering the SAME `TileContent` body against the SAME
 * tile query cache — deliberately an overlay, not grid surgery. Table tiles
 * lift their configured `rowLimit` (0 = all delivered rows; the view's own
 * MAX_RENDERED_ROWS DOM valve still applies) so the expanded view earns its
 * space. An expanded CHART registers a second SVG-export accessor under the
 * tile's runtime key; chartSvgRegistry keeps a per-key stack, so closing the
 * dialog restores the grid instance's accessor without a remount.
 */
import { useMemo } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { isQueryBackedTile, type DashboardTile } from '../types/dashboardTypes';
import { TileContent } from './tiles/TileContent';
import { TileStatusBadge } from './tiles/TileStatusBadge';
import { TileFreshnessFooter } from './tiles/TileFreshnessFooter';
import { TileChromeContext, type TileChrome } from './tiles/tileChromeContext';

export function TileExpandDialog({
  dashboardId,
  tile,
  open,
  onOpenChange,
}: {
  dashboardId: string;
  tile: DashboardTile;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const expandedTile = useMemo<DashboardTile>(
    () => (tile.tileType === 'table' ? { ...tile, rowLimit: 0 } : tile),
    [tile],
  );
  // Same chrome bridge as TileFrame — the expanded body keeps its inline
  // recovery actions and shape-aware empty state. Deliberately the ORIGINAL
  // tile (not the rowLimit-lifted render clone): a Configure/retry action
  // must never leak the expanded view's overrides into a persisted edit.
  const chrome = useMemo<TileChrome>(() => ({ dashboardId, tile }), [dashboardId, tile]);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="flex h-[86vh] flex-col gap-2 p-4 sm:max-w-[min(92vw,90rem)]"
        data-testid="tile-expand-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 pr-6 text-sm font-medium">
            <span className="truncate">{tile.title}</span>
            {isQueryBackedTile(tile) && (
              <TileStatusBadge dashboardId={dashboardId} tileId={tile.id} />
            )}
          </DialogTitle>
        </DialogHeader>
        {/* group/tile: the freshness footer reveals on hover, same as in-grid. */}
        <div className="group/tile relative min-h-0 flex-1 overflow-hidden rounded-md border border-border">
          <div className="h-full min-h-0 overflow-hidden">
            <TileChromeContext.Provider value={chrome}>
              <TileContent dashboardId={dashboardId} tile={expandedTile} />
            </TileChromeContext.Provider>
          </div>
          {isQueryBackedTile(tile) && (
            <TileFreshnessFooter dashboardId={dashboardId} tileId={tile.id} />
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
